import { saveAs } from 'file-saver';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import Vue from 'vue';
import { africaOptions } from '../globals';

const splashScreenId: string = 'splashScreen';

const showSplashScreen = (): void => {
    Vue.prototype.$modal.show({
        template: `<div class="v-modal"></div>`,
    }, {}, {
        name: splashScreenId,
        width: 0,
        height: 0,
        clickToClose: false,
    });
};

const hideSplashScreen = (): void => {
    Vue.prototype.$modal.hide(splashScreenId);
};

const showResponseErrors = (errors: Array<{statusCode: number, title: string, message: string}>): Promise<any> => {
    return new Promise((resolve) => {
        if (!Array.isArray(errors) || !errors.length) {
            return resolve(null);
        }
        hideSplashScreen();
        Vue.prototype.$modal.hide('response-errors');

        Vue.prototype.$modal.show({
            template: `
                <div class="v-modal">
                    <div class="v-modal__close" slot="top-right">
                        <button class="v-modal__close-button" @click="$emit('close')">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path d="M13 12l5-5-1-1-5 5-5-5-1 1 5 5-5 5 1 1 5-5 5 5 1-1z"></path>
                            </svg>
                        </button>
                    </div>
                    <div class="v-modal__content">
                        <div class="u-py-6 u-my-6 u-clearfix">
                            <div class="u-text-center" v-for="error in errors">
                                <h2 class="u-mt-0 u-text-strong" v-html="error.title"></h2>
                                <div v-html="error.message"></div>
                            </div>
                        </div>
                    </div>
                </div>`,
            props: ['errors'],
        }, { errors }, {
            name: 'response-errors',
            width: 460,
            height: 'auto',
            minHeight: 250,
            adaptive: true,
            pivotY: .25,
        });
    });
};

/**
 * @param {number} id Page id
 * @param {boolean} headless Use empty layout for page
 *
 * @return {Promise}
 */
function getPdf(id, headless = false): Promise<XMLHttpRequest> {
    return new Promise((resolve, reject) => {
        // We use native method for more high performance.
        const client = new XMLHttpRequest();
        client.open('POST', africaOptions.api.baseUrl + africaOptions.api.routes.site.printPage);
        client.setRequestHeader('Accept', 'application/json, application/pdf');
        client.setRequestHeader('Content-Type', 'application/json');
        client.onload = () => {
            if (client.getResponseHeader('Content-Type') === 'application/pdf') {
                // Returns object because the object is passed by reference
                resolve(client);
                return;
            }
            reject(get(JSON.parse(client.responseText), 'errors', []));
        };
        client.send(JSON.stringify({ id, headless }));
    });
}

/**
 * Returns URL component
 * @return {{createObjectURL: function, revokeObjectURL: function}}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/URL} URL
 */
function getURL() {
    return window.URL || (window as any).webkitURL || window;
}

/**
 * @see {@link: https://github.com/eligrey/FileSaver.js | FileSaver}
 */
const savePage = (id: number, isSave: boolean = true, isHeadless: boolean = false) => {
    const event = document.createEvent('Event');
    event.initEvent('readyToPrint', true, true);
    setTimeout(() => document.body.dispatchEvent(event), 1000);
    showSplashScreen();

    getPdf(id, isHeadless).then((client: XMLHttpRequest) => {
        hideSplashScreen();

        const contentType = client.getResponseHeader('Content-Type') || '';
        const sliceSize = 512;
        const byteCharacters = atob(client.responseText);
        const fileName = get((client.getResponseHeader('Content-Disposition') || '').match(/filename="(.+?)"/), 1, 'page.pdf');
        const byteArrays: Uint8Array[] = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
        }
        const file = new Blob(byteArrays, { type: contentType });
        if (isSave) {
            saveAs(file, fileName);
            return;
        }
        const url = getURL().createObjectURL(file);
        const pdf = window.open(url, '_blank', 'location=no,status=no');
        // safari blocks window open
        if (pdf !== null && isObject(pdf)) {
            // for mobile devices
            if (isFunction(pdf.focus)) {
                pdf.focus();
            }
            if (isFunction(pdf.print)) {
                pdf.print();
            }
            return;
        }
        getURL().revokeObjectURL(url);
        saveAs(file, fileName);
    }, showResponseErrors);
};

export default savePage;
